import React, { Key, useState } from 'react';
import { Plus } from '@phosphor-icons/react';
import type { ColumnsType } from 'antd/es/table';
import {
  ButtonCommon,
  ColumnSelector,
  TableCommon,
  IconSort,
  AddMaterialModal,
  ConfirmationModal,
  ExcelOperationsHandler,
  DeactivateModal,
  StatusDotCommon,
  SearchByFields,
} from 'components/common';
import { Columns, QueryParams } from 'types';
import { formatPrice } from 'utils';
import { formatDate } from 'date-fns';
import { MaterialRecord } from 'types/common';
import { useMaterialOperations } from 'hooks/useMaterialOperations ';
import { parseSimpleFilter } from 'utils/filterBuilder';
const MaterialListPage: React.FC = () => {
  const [isPerformingOperation, setIsPerformingOperation] = useState<boolean>(false);
  const params = new URLSearchParams(window.location.search);
  const isPreviewMode = params.get('preview') === 'true';

  // Use the material operations hook
  const {
    // Data
    materials,
    isFetching,
    data,

    // State
    isModalOpen,
    isDeleteModalOpen,
    materialToEdit,
    materialToDelete,
    materialToCopy,
    form,
    filteredQuery,
    isSubmitting,
    isDeactivateModalOpen,
    materialToDeactivate,
    isDeleteMode,
    isDeactivating,
    isDeletingReplacement,
    isSearchingReplacements,
    tableState,

    getActiveMaterials,
    handleReplacementSearch,
    cancelDeactivate,
    handleDeactivateWithReplacement,
    handleDeleteWithReplacement,

    // Handlers
    handleStatusToggle,
    handleEdit,
    handleCopy,
    handleDelete,
    confirmDelete,
    cancelDelete,
    handleAddNew,
    handleCancel,
    handleGenerate,
    handleSubmit,
    handlePagination,
    setFilteredQuery,
  } = useMaterialOperations();

  // Define columns for material table
  const materialColumns: ColumnsType<MaterialRecord> = [
    {
      title: 'MÃ',
      dataIndex: 'code',
      key: 'code',
      sorter: (a: MaterialRecord, b: MaterialRecord) => a.code.localeCompare(b.code),
      showSorterTooltip: false,
      filterSearch: true,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (text) => (
        <div className='font-mono' style={{ fontWeight: 600, color: 'var(--textBlack)' }}>
          {text}
        </div>
      ),
    },
    {
      title: 'TÊN NGUYÊN LIỆU',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: MaterialRecord, b: MaterialRecord) => a.name.localeCompare(b.name),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      filterSearch: true,
    },
    {
      title: 'SỐ LƯỢNG',
      dataIndex: 'quantity',
      key: 'quantityMaterial',
      sorter: (a: MaterialRecord, b: MaterialRecord) => (a.quantity || 0) - (b.quantity || 0),
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (quantity: number) => <div>{formatPrice(quantity || 0)}</div>,
      filterSearch: false,
    },
    {
      title: 'GIÁ NHẬP',
      dataIndex: 'costPrice',
      key: 'costPrice',
      sorter: (a: MaterialRecord, b: MaterialRecord) => a.costPrice - b.costPrice,
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (price: number) => <div>{formatPrice(price)} VNĐ</div>,
      filterSearch: false,
    },
    {
      title: 'GIÁ BÁN',
      dataIndex: 'salePrice',
      key: 'salePrice',
      sorter: (a: MaterialRecord, b: MaterialRecord) => a.salePrice - b.salePrice,
      showSorterTooltip: false,
      sortIcon(props) {
        return <IconSort props={props} />;
      },
      render: (price: number) => <div>{formatPrice(price)} VNĐ</div>,
      filterSearch: false,
    },
    {
      title: 'TRẠNG THÁI',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 200,
      filterSearch: true,
      filters: [
        { text: 'Đang kinh doanh', value: true },
        { text: 'Ngừng kinh doanh', value: false },
      ],
      onFilter: (value, record) => record.isActive === value,
      render: (isActive: boolean) => {
        return isActive ? (
          <StatusDotCommon dotBgColor='bg-[var(--sematicSuccessColor500)]' label='Đang kinh doanh' />
        ) : (
          <StatusDotCommon dotBgColor='bg-[var(--sematicErrorColor500)]' label='Ngừng kinh doanh' />
        );
      },
    },
    {
      title: 'NGÀY TẠO',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date) => <span>{formatDate(date, 'dd/MM/yyyy')}</span>,
    },
  ];

  const materialColumnsCheck: Columns[] = materialColumns.map((column) => ({
    key: column.key as string,
    title: column.title as string,
  }));

  const [checkedList, setCheckedList] = useState<(Key | undefined)[]>(materialColumns.map((column) => column.key));

  const getFilteredColumns = () => {
    return materialColumns.map((item) => ({
      ...item,
      hidden: !checkedList.includes(item.key),
    }));
  };

  // Wrap handlers to show loading state during operations
  const wrappedHandlers = {
    handleStatusToggle: async (checked: boolean, record: MaterialRecord) => {
      setIsPerformingOperation(true);
      try {
        await handleStatusToggle(checked, record);
      } finally {
        setIsPerformingOperation(false);
      }
    },

    handleDelete: (record: MaterialRecord) => {
      setIsPerformingOperation(true);
      try {
        handleDelete(record);
      } finally {
        setIsPerformingOperation(false);
      }
    },

    confirmDelete: async () => {
      setIsPerformingOperation(true);
      try {
        await confirmDelete();
      } finally {
        setIsPerformingOperation(false);
      }
    },

    handleReplaceMaterial: async (replacementMaterialId: string) => {
      setIsPerformingOperation(true);
      try {
        if (isDeleteMode) {
          await handleDeleteWithReplacement(replacementMaterialId);
        } else {
          await handleDeactivateWithReplacement(replacementMaterialId);
        }
      } finally {
        setIsPerformingOperation(false);
      }
    },
  };

  const getReplaceModalTitle = () => {
    if (isDeleteMode) {
      return 'Xóa nguyên liệu và thay thế';
    }
    return 'Hủy kích hoạt nguyên liệu';
  };

  return (
    <div className='flex flex-col h-full relative'>
      {/* Add Material Modal */}
      <AddMaterialModal
        isOpen={isModalOpen}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onGenerate={handleGenerate}
        form={form}
        editMode={!!materialToEdit}
        materialData={materialToEdit}
        isSubmitting={isSubmitting || isPerformingOperation}
        isCopyMode={!!materialToCopy}
      />

      {/* Deactivate/Replace Modal */}
      <DeactivateModal
        isOpen={isDeactivateModalOpen}
        onCancel={cancelDeactivate}
        onConfirm={wrappedHandlers.handleReplaceMaterial}
        activeRecords={getActiveMaterials()}
        recordToDeactivate={materialToDeactivate}
        isUpdating={isPerformingOperation || isDeactivating || isDeletingReplacement}
        title={getReplaceModalTitle()}
        entityName='nguyên liệu'
        onSearch={handleReplacementSearch}
        isSearchLoading={isSearchingReplacements}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onCancel={cancelDelete}
        onConfirm={wrappedHandlers.confirmDelete}
        title='Xác nhận xóa nguyên liệu'
        subtitle={
          <div>
            Bạn có chắc chắn muốn xóa nguyên liệu
            <span className='font-semibold'> {materialToDelete?.name}</span>?
            <br />
            Hành động này không thể hoàn tác.
          </div>
        }
        icon='delete'
        confirmText='Xóa'
        confirmLoading={isPerformingOperation}
      />

      <div className='w-full bg-[var(--textWhite)]'>
        <div className='flex lg:flex-row flex-col gap-2 justify-between items-center px-[24px] py-[12px]'>
          <h1 className='text-[18px] leading-[26px] font-[700]'>DANH SÁCH NGUYÊN LIỆU</h1>
          <div className='flex gap-2'>
            <div className='flex-1 max-w-[250px]'>
              <SearchByFields
                filteredQuery={filteredQuery}
                setFilteredQuery={(query) => setFilteredQuery({ ...query, page: 1 } as unknown as QueryParams)}
                searchFields={['name', 'code']}
                placeholder='Tìm kiếm theo tên hoặc mã...'
              />
            </div>
            <ColumnSelector
              allColumns={materialColumnsCheck}
              selectedColumns={checkedList}
              onChange={(selected) => setCheckedList(selected)}
            />

            <ExcelOperationsHandler entityType='material' entityName='nguyên liệu' disabled={isPerformingOperation} />

            <ButtonCommon icon={<Plus size={20} />} color='primary' onClick={handleAddNew} size='xs' radius='rounded'>
              Thêm nguyên liệu mới
            </ButtonCommon>
          </div>
        </div>
      </div>

      <div className='flex-grow bg-[var(--textWhite)] flex flex-col'>
        <div className='flex-grow flex flex-col'>
          <TableCommon<MaterialRecord>
            columns={getFilteredColumns()}
            dataSource={materials}
            defaultPageSize={Number(filteredQuery.size) || 10}
            onEdit={handleEdit}
            onDelete={wrappedHandlers.handleDelete}
            onCopy={handleCopy}
            count={data?.data?.count || materials.length}
            tableProps={{
              scroll: { x: 'max-content' },
              size: 'large',
              className: 'h-[calc(100vh - 290px)]',
              style: {
                minHeight: `calc(100vh - ${!isPreviewMode ? '290px' : '200px'}) `,
                display: 'flex',
                flexDirection: 'column',
              },
              loading: isFetching,
            }}
            onPaginationChange={handlePagination}
            handleToggleStatus={wrappedHandlers.handleStatusToggle}
            currentPage={Number(filteredQuery.page)}
            searchColumnDefault={parseSimpleFilter(tableState?.filter)}
          />
        </div>
      </div>
    </div>
  );
};

export default MaterialListPage;
